<template>
  <div id="pulsePage">
    <!-- Responsive layout -->
    <div
      v-if="scene < 6"
      :class="['desktop', 'laptop'].includes($mq) ? 'pageDesk' : 'pageMobile'"
    >
      <div
        class="customPagination"
        :class="['desktop', 'laptop'].includes($mq) ? 'deskMode' : 'mobileMode'"
      >
        <!-- Pagination hidden -->
        <el-pagination
          layout="prev, pager, next"
          :next-text="['desktop', 'laptop'].includes($mq) ? 'Next' : ''"
          :prev-text="['desktop', 'laptop'].includes($mq) ? 'Prev' : ''"
          :current-page="scene"
          :page-size="1"
          :total="5"
          @current-change="currentPage"
        />
        <!-- Visual progress but placed under the pagination -->
        <el-progress
          :percentage="percentageFilter"
          :color="'#00ace2'"
          :show-text="false"
        />
      </div>

      <!-- Navigator -->
      <div class="sceneCounter">
        Coach Value Contribution Review
      </div>

      <Carousel
        :per-page="1"
        :navigate-to="scene - 1"
        :mouse-drag="['mobile', 'tablet'].includes($mq)"
        :touch-drag="['mobile', 'tablet'].includes($mq)"
        @page-change="currentPage($event + 1)"
      >
        <Slide
          v-for="(pageData, pageKey) in pageFilter.slide"
          :key="pageKey"
        >
          <div
            :key="pageKey"
            class="eachPage"
            :class="`${pageKey < scene - 1 || scene - 1 < pageKey ? 'outRange' : ''}`"
          >
            <!-- Description with leader data -->
            <div
              v-if="pageData.type === 'textarea'"
              class="description"
            >
              <span
                v-for="(list, descKey) in pageData.list"
                :key="descKey"
              >
                {{ list.number }}. {{ $t(`cvc.question_${list.number}`) }}
              </span>
            </div>

            <!-- Question list divide by type -->
            <div class="questionWrap">
              <!-- Step type -->
              <div
                v-if="pageData.type === 'step'"
                class="questionSelect cvcStep"
              >
                <div class="questionTitleWrap">
                  <div class="titleArea">
                    <template v-for="key in 5">
                      <div
                        :key="`cvcScore${key}`"
                        class="placer"
                      >
                        <div class="placedText">
                          {{ $t(`cvc.score_${key}`) }}
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <div
                  v-for="(radio, radioKey) in pageData.list"
                  :key="`Radio${radioKey}`"
                  class="questionContentsList"
                  :class="pageSource.blankList.includes(`q${radio.number}`)
                    ? 'redAlert' : ''"
                >
                  <div class="listTitle">
                    {{ radio.number }}. {{ $t(`cvc.question_${radio.number}`) }}
                  </div>

                  <el-radio-group
                    v-if="pageSource"
                    v-model="pageSource.answer[`q${radio.number}`]"
                    class="listRadio"
                    :class="['desktop', 'laptop'].includes($mq) ? 'deskRadio' : 'mobileRadio'"
                    @change="radioToKey(`q${radio.number}`, $event)"
                  >
                    <el-radio-button
                      v-for="(item, key) in pageSource.step.cvc"
                      :key="key"
                      :label="item.score"
                    >
                      <div class="radioScore">
                        <i
                          v-if="['desktop', 'laptop'].includes($mq)"
                          class="el-icon-check"
                        ></i>
                        <span v-else>
                          {{ item.desc }}
                        </span>
                      </div>
                    </el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              <!-- /Step type -->

              <!-- Text area type -->
              <div
                v-else-if="pageData.type === 'textarea'"
                class="questionSelect"
              >
                <div
                  v-if="pageSource"
                  class="questionText"
                >
                  <el-input
                    v-model="pageSource.answer[`q${pageData.list[0].number}`]"
                    class="questionArea"
                    type="textarea"
                    placeholder=""
                    maxlength="1000"
                    rows="8"
                    show-word-limit
                  />
                  <el-button
                    v-if="pageData.page === 5"
                    class="textButton"
                    type="text"
                    @click="currentPage(pageData.page + 1)"
                  >
                    {{ $t('common.done') }}
                  </el-button>
                </div>
              </div>
              <!-- /Text area type -->
            </div>
          </div>
        </Slide>
      </Carousel>
    </div>
    <!-- /Responsive layout -->

    <div
      v-else
      class="staticPage"
    >
      <div class="staticPageContents">
        <div
          class="staticDesc"
          :class="pageFilter.static.list[0].number !== 'submit'
            && pageSource.blankList.includes(`${pageFilter.static.list[0].number}`)
            ? 'redAlert' : ''"
        >
          {{ $t(`cvc.question_${pageFilter.static.list[0].number}`) }}
        </div>

        <!-- Page 6 radio -->
        <div
          v-if="pageFilter.static.page === 6 && pageSource"
          class="staticRadio"
          :class="pageSource.blankList.includes(`q${pageFilter.static.list[0].number}`)
            ? 'redAlert' : ''"
        >
          <el-radio
            v-model="pageSource.answer[`${pageFilter.static.list[0].number}`]"
            :label="true"
            @change="radioToKey(`${pageFilter.static.list[0].number}`, $event)"
          >
            {{ $t('common.yes') }}
          </el-radio>
          <el-radio
            v-model="pageSource.answer[`${pageFilter.static.list[0].number}`]"
            :label="false"
            @change="radioToKey(`${pageFilter.static.list[0].number}`, $event)"
          >
            {{ $t('common.no') }}
          </el-radio>
        </div>

        <!-- Page 6 buttons -->
        <div
          v-if="pageFilter.static.page === 6"
          class="staticButtons"
        >
          <el-button
            type="primary"
            round
            @click="currentPage(7)"
          >
            {{ $t('common.done') }}
          </el-button>
          <el-button
            type="info"
            round
            @click="currentPage(5)"
          >
            {{ $t('common.goBack') }}
          </el-button>
        </div>

        <!-- Page 7 buttons -->
        <div
          v-else-if="pageFilter.static.page === 7"
          class="staticButtons"
        >
          <el-button
            type="primary"
            round
            @click="submitPulse"
          >
            {{ $t('common.submit') }}
          </el-button>
          <el-button
            type="info"
            round
            @click="currentPage(5)"
          >
            {{ $t('common.goBack') }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Carousel, Slide } from 'vue-carousel';

export default {
  components: {
    Carousel,
    Slide,
  },

  props: {
    role: {
      type: String,
      default: 'stakeholder',
    },
    pulseData: {
      type: Object,
      default: () => ({
        coach: {
          first_name: '',
          last_name: '',
        },
      }),
    },
    scene: {
      type: Number,
      default: 1,
    },
    submit: {
      type: Boolean,
      default: false,
    },
    pageStructure: {
      type: Array,
      default: null,
    },
    pageSource: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    pageFilter() {
      const filteredData = {
        slide: [],
        static: {},
      };

      if (this.pageStructure) {
        this.pageStructure.forEach((info) => {
          if (info.page < 6) {
            filteredData.slide.push(info);
          } else if (this.scene === info.page) {
            filteredData.static = info;
          }
        });
      }

      return filteredData;
    },

    percentageFilter() {
      let percent = 0;

      if (this.scene) {
        const value = Math.round(this.scene * 20);

        if (value > 100) {
          percent = 100;
        } else if (value < 0) {
          percent = 0;
        } else {
          percent = value;
        }
      }

      return percent;
    },
  },

  methods: {
    submitPulse() {
      this.$emit('submitPulse');
    },

    radioToKey(option, score) {
      this.pageSource.answer[option] = score;

      if (!this.pageSource.firstTry) {
        this.$emit('validationPulse');
      }

      if (['q5', 'q7'].includes(option)) {
        // Delayed auto page scroll
        setTimeout(() => {
          this.$emit('update:scene', this.scene + 1);
        }, 400);
      }
    },

    currentPage(page) {
      // Pagination with number params
      this.$emit('update:scene', page);
    },
  },
};
</script>

<style lang="scss" scoped>
  #pulsePage {
    .pageDesk {
      position: relative;

      .sceneCounter {
        font-size: 16px;
        color: black;
        line-height: 17px;
      }

      .description {
        font-size: 15px;
        padding: 25px 0 5px;

        &.redAlert {
          color: red;
        }
      }

      .VueCarousel {
        padding-bottom: 40px;
      }

      .VueCarousel-slide {
        padding: 5px 10px;
        box-sizing: border-box;
      }

      .outRange {
        max-height: 10px;
      }

      .questionWrap {
        padding: 15px 0;

        .questionTitle {
          padding: 10px 0;
        }

        .questionSelect {
          .questionTitleWrap {
            width: 100%;

            .titleArea {
              width: 60%;
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-left: auto;
              height: 50px;

              .placer {
                position: relative;
                display: flex;
                width: 45px;
                height: 50px;
                box-sizing: border-box;
                align-items: center;

                .placedText {
                  width: 60px;
                  position: absolute;
                  left: 50%;
                  transform: translateX(-50%);
                  font-size: 12px;
                  text-align: center;
                }
              }
            }
          }

          .questionContentsList {
            display: flex;
            padding: 20px 0;

            .listTitle {
              width: 40%;
              padding-right: 15px;
              box-sizing: border-box;
            }

            .listRadio {
              display: flex;
              width: 60%;
              justify-content: space-between;
              align-items: center;

              .el-radio-button {
                width: 45px;
                height: 45px;
                border: 2px solid #00ace2;
                border-radius: 100%;
                overflow: hidden;
              }
            }

            &.redAlert {
              color: red;

              .el-radio-button {
                border-color: red;
              }
            }
          }
        }

        .questionText {
          position: relative;

          .questionArea {
            position: relative;
            top: 0;
            left: 0;
            border-radius: 5px;
            box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
          }

          .textButton {
            position: absolute;
            width: 60px;
            font-weight: bold;
            background: white;
            bottom: 0;
            right: 0;
          }
        }
      }
    }

    .pageMobile {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: auto;
      background: white;

      .sceneCounter {
        font-size: 16px;
        color: black;
        line-height: 17px;
        padding: 20px 20px 5px;
      }

      .sceneWrap {
        position: relative;
      }

      .eachPage {
        padding: 20px;
        box-sizing: border-box;

        .description {
          font-size: 14px;
          padding: 10px 0;

          &.redAlert {
            color: red;
          }
        }

        .questionWrap {
          padding: 15px 0;

          .questionTitle {
            padding: 10px 0;
          }

          .questionSelect {
            .questionTitleWrap {
              display: none;
            }

            .questionContentsList {
              padding: 5px 0 15px;

              .listTitle {
                padding: 0 0 10px;
              }

              .el-radio-group {
                width: 100%;

                .el-radio-button {
                  width: 100%;
                  padding: 3px 0;

                  &:focus {
                    outline: none !important;
                    box-shadow: none !important;
                  }
                }
              }
            }
          }

          .questionText {
            position: relative;

            .questionArea {
              position: relative;
              top: 0;
              left: 0;
              border-radius: 5px;
              border: 1px solid #00ace2;
            }

            .textButton {
              position: absolute;
              width: 60px;
              font-weight: bold;
              bottom: 0;
              right: 0;
            }
          }
        }

        &.outRange {
          height: 10px;
          min-height: calc(100vh - 100px);
        }
      }
    }

    .staticPage {
      .staticPageContents {
        .staticDesc {
          padding: 10px 0;
          text-align: center;

          &.redAlert {
            color: red;
          }
        }

        .staticRadio {
          padding: 10px 0;
          text-align: center;
        }

        .staticButtons {
          display: flex;
          justify-content: center;
          padding-top: 20px;

          .el-button {
            padding: 13px 0;
            width: 50%;
            max-width: 180px;
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  #pulsePage {
    .VueCarousel-pagination {
      display: none !important;
    }

    .pageDesk {
      .listRadio {
        .el-radio-button {
          .el-radio-button__inner {
            width: 100%;
            height: 100%;
            padding: 0;
            border: none;
            background: unset;
            display: flex;
            justify-content: center;
            align-items: center;

            .radioScore {
              padding-top: 3px;
              visibility: hidden;
            }
          }

          &.is-active {
            .el-radio-button__inner {
              background: #00ace2;

              .radioScore {
                visibility: visible;
                font-size: 22px;
              }
            }
          }
        }
      }

      .questionText {
        .questionArea {
          .el-textarea__inner {
            position: relative;
            border: none;
            padding: 15px 15px 0;
            margin-bottom: 40px;
            resize: none;
          }

          .el-input__count {
            left: 0 !important;
            bottom: 0;
            border-radius: 5px;
            padding: 14px;
            right: 0;
          }
        }
      }
    }

    .pageMobile {
      .eachPage {
        .description {
          &.redAlert {
            color: red;
          }
        }

        .VueCarousel-slide {
          padding: 5px 10px;
          box-sizing: border-box;
        }

        .questionWrap {
          .questionSelect {
            .questionContentsList {
              .el-radio-group {
                .el-radio-button {
                  .el-radio-button__inner {
                    position: relative;
                    width: 100%;
                    padding: 15px;
                    border-radius: 5px;
                    border: 1px solid #00ace2;
                    font-size: 14px;
                    text-align: left;

                    .radioScore {
                      display: inline-block;
                      width: 22px;
                    }

                    .radioDesc {
                      display: inline-block;
                      color: black;
                      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
                    }

                    .radioActive {
                      position: absolute;
                      display: inline-block;
                      right: 10px;
                      visibility: hidden;

                      i {
                        font-size: 20px;
                      }
                    }
                  }

                  &.is-active {
                    .el-radio-button__inner {
                      background: #00ace2;

                      .radioDesc {
                        color: white;
                      }

                      .radioActive {
                        visibility: visible;
                      }
                    }
                  }
                }
              }

              &.redAlert {
                color: red;

                .el-radio-group {
                  .el-radio-button {
                    .el-radio-button__inner {
                      border-color: red;
                    }
                  }
                }
              }
            }
          }

          .questionText {
            .questionArea {
              .el-textarea__inner {
                position: relative;
                border: none;
                padding: 15px 15px 0;
                margin-bottom: 40px;
                resize: none;
              }

              .el-input__count {
                left: 0 !important;
                bottom: 0;
                border-radius: 5px;
                padding: 14px;
                right: 0;
              }
            }
          }
        }
      }
    }

    .staticPage {
      .staticPageContents {
        .staticRadio {
          .el-radio {
            &.is-checked {
              .el-radio__inner {
                background: #00ace2;
                border-color: #00ace2;
              }

              .el-radio__label {
                color: #00ace2;
              }
            }
          }

          &.redAlert {
            .el-radio {
              .el-radio__label {
                color: red;
              }
            }
          }
        }
      }
    }

    // Desk
    .customPagination {
      .el-pagination {
        display: flex;
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        justify-content: center;
        z-index: 2000;
        padding: 5px 0 15px;

        .el-pager {
          .number {
            font-size: 0;
            min-width: 1px;
            min-height: 1px;
            width: 20%;

            // &:last-child {
            //   display: none;
            // }
          }
        }
      }

      &.deskMode {
        position: absolute;
        width: 100%;
        bottom: -20px;

        .el-pagination {
          .el-pager {
            width: 81%;
          }

          button {
            color: #00ace2 !important;
            padding: 0 10px;

            &.btn-prev {
              padding-left: 0;
            }

            &.btn-next {
              padding-right: 0;
            }

            &:disabled {
              color: #a1a1a1 !important;
            }

            .el-icon {
              font-size: 22px;
            }

            span {
              font-size: 16px;
              line-height: 28px;
            }
          }
        }

        .el-progress {
          left: 0;
          bottom: 0;
          width: 100%;
          z-index: 1800;
          padding: 26px 0;

          .el-progress-bar {
            width: 80%;
            margin: 0 auto;

            .el-progress-bar__outer {
              background: #c4c4c4;
            }
          }
        }
      }

      &.mobileMode {
        position: relative;
        top: 0;

        .el-pagination {
          .el-pager {
            width: 85%;
          }

          .btn-prev,
          .btn-next {
            color: #00ace2 !important;
            padding: 0 10px;

            &:disabled {
              color: #a1a1a1 !important;
            }

            .el-icon {
              font-size: 22px;
            }
          }
        }

        .el-progress {
          // position: fixed;
          left: 0;
          bottom: 0;
          width: 100%;
          z-index: 1800;
          padding: 26px 0;
          background: white;

          .el-progress-bar {
            width: 84%;
            margin: 0 auto;
          }
        }
      }
    }
  }
</style>
